import { Box } from '@mui/material';
import { DataGridProProps, GridColDef, GridColumnGroupingModel, GridRenderCellParams } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { CSSProperties, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { StyleObj } from '../../../@types';
import { PlayerReport } from '../../../@types/api';
import { roleRequiresManager } from '../../../constants/keycloak';
import { appendTotalsRow, generateDateRangeTimestamps, getTotalsRowClass } from '../../../helpers';
import { createCommissionColumn, createNumberColumn, createPercentageColumn } from '../../../helpers/columns';
import { defaultColumnsAffiliatePlayers, defaultColumnsPlayersReports } from '../../../helpers/table';
import { usePagination } from '../../../hooks/usePagination';
import usePermissions from '../../../hooks/usePermissions';
import useSort from '../../../hooks/useSort';
import { createColumn } from '../../../utils';
import DateRangeSelect, { DayjsTimestamp } from '../../molecules/DateRangeSelect';
import AgentReportsDetails from '../../organisms/AgentReportsDetails';
import { defaultMonitoringFilters } from '../../organisms/forms/FilterTicketMonitoring';
import TableTemplate from '../../templates/TableTemplate';
import { Launch } from '@mui/icons-material';
import { BETTING_ACTIVITY } from '../../../constants/enums';
import API_ROUTES from '../../../constants/apiRoutes';
import { useMarketplace } from '../../../hooks/useMarketplace';

const styles: StyleObj = {
  container: {
    position: 'relative',
    height: 'calc(100% - 200px)',
  },
  table: {
    border: 'none',
    '& .MuiDataGrid-columnHeader': {
      border: '1px solid rgba(0, 83, 55, 0.20)',
      backgroundColor: 'background.lightGreen',
      '&:first-of-type': {
        borderRadius: 0,
      },
      '&:last-of-type': {
        borderRadius: 0,
        width: '100% !important',
        maxWidth: 'unset !important',
      },
      '& .MuiDataGrid-sortIcon': {
        color: 'grey.700',
      },
    },
    '& .MuiDataGrid-columnHeadersInner': {
      width: '100%',
      '> div': {
        width: '100%',
      },
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 700,
      color: 'grey.700',
    },
    '& .MuiDataGrid-row:nth-of-type(even)': {
      backgroundColor: 'background.light',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
      },
    },
    '& .MuiDataGrid-columnHeaders:hover .MuiDataGrid-columnSeparator': {
      visibility: 'hidden',
    },
    '& .customGroupHeader': {
      backgroundColor: 'primary.main',
      '& .MuiDataGrid-columnHeaderTitleContainer': {
        justifyContent: 'center',
      },
      '& .MuiDataGrid-columnHeaderTitle': {
        fontWeight: 700,
        color: 'white',
      },
    },
    '& .MuiDataGrid-columnHeader--emptyGroup': {
      backgroundColor: 'primary.main',
      '&:first-of-type': {
        borderTopLeftRadius: 8,
      },
      '&:last-of-type': {
        borderTopRightRadius: 8,
      },
    },
  },
  link: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    textDecoration: 'none',
    color: 'inherit',
  },
};

const columnGroupingModel: GridColumnGroupingModel = [
  {
    groupId: 'Tickets',
    description: 'Tickets',
    headerClassName: 'customGroupHeader',
    children: [
      { field: 'mainTicketCount' },
      { field: 'bonusTicketCount' },
      { field: 'totalTicketCount' },
      { field: 'openTicketCount' },
      { field: 'wonTicketCount' },
      { field: 'lostTicketCount' },
    ],
  },
  {
    groupId: 'Stakes',
    description: 'Stakes',
    headerClassName: 'customGroupHeader',
    children: [
      { field: 'mainStakeAmount' },
      { field: 'bonusStakeAmount' },
      { field: 'totalStakeAmount' },
      { field: 'totalCommissionAmount' },
    ],
  },
  {
    groupId: 'Winnings',
    description: 'Winnings',
    headerClassName: 'customGroupHeader',
    children: [
      { field: 'mainWinningAmount' },
      { field: 'bonusWinningAmount' },
      { field: 'jackpotAmount' },
      { field: 'totalWinningAmount' },
    ],
  },
  {
    groupId: 'Profit',
    description: 'Profit',
    headerClassName: 'customGroupHeader',
    children: [
      { field: 'mainProfitAmount' },
      { field: 'bonusProfitAmount' },
      { field: 'totalProfitAmount' },
      { field: 'ngr' },
      { field: 'profitMarginPercentage' },
      { field: 'totalAfterCommission' },
    ],
  },
];

const generateTotalsRow = (data?: PlayerReport) => {
  if (data) {
    return {
      ...data,
      id: 'totals',
      userId: 'totals',
      username: 'Totals',
      isTotalsRow: true,
    };
  }
};

const generateRowIds = (data: PlayerReport[]) => {
  return data.map((row, index) => ({
    ...row,
    id: `${row.userId}-${index}`,
  }));
};

const calculateProfitAfterCommission = (report: PlayerReport) => {
  const profit = Number(report.totalProfitAmount) || 0;
  const commission = Number(report.totalCommissionAmount) || 0;
  return profit - commission;
};

const getMobileCardHeaderItems = (row: PlayerReport) => {
  const afterCommission = calculateProfitAfterCommission(row);

  return [
    { value: row.username, flex: 1 },
    { label: 'Profit', value: row.totalProfitAmount.toFixed(2) },
    { label: 'After Comm.', value: afterCommission.toFixed(2) },
  ];
};

const PlayersReportsPage = () => {
  const [selectedRow, setSelectedRow] = useState<PlayerReport | null>(null);

  const { hasPermission, role, isAffiliate } = usePermissions();
  const { hasAffiliateSystem } = useMarketplace();

  const defaultColumns = isAffiliate ? defaultColumnsAffiliatePlayers : defaultColumnsPlayersReports;

  const { fromTimestamp, toTimestamp } = generateDateRangeTimestamps('today');
  const [date, setDate] = useState<DayjsTimestamp>({
    fromTimestamp: dayjs().startOf('day'),
    toTimestamp: dayjs().endOf('day'),
  });

  const { data, updateQueryParams, isFetching, isLoading, changeQuery } = usePagination<PlayerReport>(
    API_ROUTES.REPORTS.PLAYERS,
    {
      page: 1,
      limit: 25,
      fromTimestamp: fromTimestamp.valueOf(),
      toTimestamp: toTimestamp.valueOf(),
      bettingActivity: BETTING_ACTIVITY.ACTIVE,
    }
  );

  const { handleSort } = useSort(changeQuery);
  const canViewNgr = hasPermission('viewNgrReportColumn') && hasAffiliateSystem;

  const totalsRow = generateTotalsRow(data?.totals);
  const rows = generateRowIds(data?.items || []);
  const aggregatedData = appendTotalsRow(rows, totalsRow);

  const handleSearch = (value: string | null) => {
    changeQuery({ search: value });
  };

  const handleDateChange = (newDate: DayjsTimestamp) => {
    setDate(newDate);
  };

  useEffect(() => {
    changeQuery({
      fromTimestamp: date.fromTimestamp.valueOf(),
      toTimestamp: date.toTimestamp.valueOf(),
    });
  }, [date, changeQuery]);

  const onCommissionCellClick = (params: GridRenderCellParams<PlayerReport>) => {
    setSelectedRow(params.row);
  };

  const columns: GridColDef[] = [
    createColumn('username', 'Player', {
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<PlayerReport>) => {
        if (params.row.userId === 'totals') return params.value;

        return (
          <Link
            to='/sports-betting/offer-management/ticket-monitoring'
            state={{ defaultFilters: { ...defaultMonitoringFilters, userId: params.row.userId } }}
            style={styles.link as CSSProperties}
          >
            {params.value} <Launch fontSize='small' color='primary' />
          </Link>
        );
      },
    }),
    createCommissionColumn(onCommissionCellClick),
    createColumn('mainTicketCount', 'Main'),
    createColumn('bonusTicketCount', 'Credit'),
    createColumn('totalTicketCount', 'Total'),
    createColumn('openTicketCount', 'Open'),
    createColumn('wonTicketCount', 'Won'),
    createColumn('lostTicketCount', 'Lost'),
    createNumberColumn('mainStakeAmount', 'Main'),
    createNumberColumn('bonusStakeAmount', 'Credit'),
    createNumberColumn('totalStakeAmount', 'Total'),
    createNumberColumn('totalCommissionAmount', 'Total comm.'),
    createNumberColumn('mainWinningAmount', 'Main'),
    createNumberColumn('bonusWinningAmount', 'Credit'),
    createNumberColumn('jackpotAmount', 'Jackpots'),
    createNumberColumn('totalWinningAmount', 'Total'),
    createNumberColumn('mainProfitAmount', 'Main'),
    createNumberColumn('bonusProfitAmount', 'Credit'),
    createNumberColumn('totalProfitAmount', 'Total'),
    ...(canViewNgr ? [createNumberColumn('ngr', 'NGR', { sortable: false })] : []),
    createPercentageColumn('profitMarginPercentage', 'Profit Margin %'),
    createNumberColumn('totalAfterCommission', 'Total after comm.', {
      valueGetter: (value, row, column, apiRef) => calculateProfitAfterCommission(row),
    }),
  ];

  return (
    <Box sx={styles.container}>
      <TableTemplate
        columnGroupingModel={columnGroupingModel}
        rows={aggregatedData}
        columns={columns}
        sx={styles.table as DataGridProProps['sx']}
        loading={isFetching || isLoading}
        rowCount={data?.count || 0}
        handlePaginationModelChange={updateQueryParams}
        handleSearch={handleSearch}
        handleSort={handleSort}
        getRowClassName={getTotalsRowClass}
        changeQuery={changeQuery}
        mobileCardHeaderItems={getMobileCardHeaderItems}
        defaultVisibleColumns={defaultColumns}
        showExport
        customTableHeaderContent={<DateRangeSelect date={date} onDateChange={handleDateChange} />}
        disableFilter={roleRequiresManager(role)}
      />
      {selectedRow?.commission?.info && (
        <AgentReportsDetails
          closeModal={() => setSelectedRow(null)}
          dateRangeFilter={date}
          commission={selectedRow.commission.info}
          id={selectedRow.agentId || ''}
          playerId={selectedRow.userId}
        />
      )}
    </Box>
  );
};

export default PlayersReportsPage;
